/* eslint-disable react/jsx-no-bind */
import { Key, Pencil, TrashBin } from '@gravity-ui/icons'
import { Button, Chip, Table, toast, Tooltip } from '@heroui/react'
import { useTranslation } from 'react-i18next'

import { authApi } from '@/api/auth.api'
import { UserAvatar } from '@/components/common/UserAvatar'
import type { UiUser } from '@/store/users.store'

interface UserTableRowProps {
  user: UiUser;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
}

export function UserTableRow({ user: u, onEdit, onDelete }: UserTableRowProps) {
  const { t } = useTranslation()

  const getRoleColor = (role: string): "default" | "accent" | "success" | "warning" | "danger" | undefined => {
    switch (role) {
      case 'admin':
        return 'danger'
      case 'accountant':
        return 'accent'
      case 'warehouse':
        return 'warning'
      default:
        return 'default'
    }
  }

  const handleResetPassword = async () => {
    try {
      await authApi.forgotPassword({ email: u.email })
      toast.success(
        t('users.reset_password_success', { email: u.email })
      )
    } catch (err) {
      toast.danger((err as Error).message || t('common.error', 'Ошибка'))
    }
  }

  return (
    <Table.Row key={u.id} className="cursor-pointer">
      <Table.Cell onClick={() => onEdit(u.id)}>
        <div className="flex items-center gap-3">
          <UserAvatar name={u.name} size="sm" />
          <span className="font-semibold truncate">{u.name}</span>
        </div>
      </Table.Cell>
      <Table.Cell onClick={() => onEdit(u.id)}>
        <span className="font-mono text-xs">{u.email}</span>
      </Table.Cell>
      <Table.Cell onClick={() => onEdit(u.id)}>
        <Chip
          color={getRoleColor(u.role)}
          size="sm"
          variant="soft"
          className="capitalize font-semibold"
        >
          {t(`users.roles.${u.role}`, u.role)}
        </Chip>
      </Table.Cell>
      <Table.Cell onClick={() => onEdit(u.id)}>
        <Chip
          color={u.verified ? 'success' : 'warning'}
          size="sm"
          variant="soft"
          className="font-semibold"
        >
          {u.verified ? t('users.verified_yes') : t('users.verified_no')}
        </Chip>
      </Table.Cell>

      <Table.Cell>
        <div className="flex justify-end gap-2" onClick={e => e.stopPropagation()}>
          <Tooltip delay={0}>
            <Button
              isIconOnly size="sm" variant="tertiary" onClick={handleResetPassword}
            >
              <Key className="size-4" />
            </Button>
            <Tooltip.Content>
              <p>{t('users.reset_password')}</p>
            </Tooltip.Content>
          </Tooltip>

          <Tooltip delay={0}>
            <Button
              isIconOnly size="sm" variant="tertiary" onClick={() => onEdit(u.id)}
            >
              <Pencil className="size-4" />
            </Button>
            <Tooltip.Content>
              <p>{t('common.edit')}</p>
            </Tooltip.Content>
          </Tooltip>

          <Tooltip delay={0}>
            <Button
              isIconOnly size="sm" variant="danger-soft" onClick={() => onDelete(u.id)}
            >
              <TrashBin className="size-4" />
            </Button>
            <Tooltip.Content>
              <p>{t('common.delete')}</p>
            </Tooltip.Content>
          </Tooltip>
        </div>
      </Table.Cell>
    </Table.Row>
  )
}
